// frontend/src/api/publicShare.ts
/**
 * The public share page's one request (design 8.5-8.6): GET /api/public/share/{token}.
 *
 * Plain `fetch`, never apiClient: apiClient adds the visitor's Bearer token
 * when they happen to be logged in, and on a 401 sends them to /login. This
 * request carries no credentials (`credentials: 'omit'`, no Authorization)
 * and no Referer. Every failure the server has (a malformed, unknown, revoked
 * or expired token, a deleted photo) is one uniform 404, read here as "gone".
 */

import { MAX_ROTATION, MAX_SIZE, MIN_SIZE } from '@/features/livestock/framing';
import { buildApiUrl } from './config';
import type { LivestockType } from '@/types/livestock';
import type {
  PublicShare,
  PublicShareSlot,
  PublicShareSlotName,
  PublicShareVariant,
} from '@/types/shareLinks';

/** secrets.token_urlsafe(16): 22 characters (the server checks the same). */
export const SHARE_TOKEN_PATTERN = /^[A-Za-z0-9_-]{22}$/;

const PUBLIC_PREFIX = '/api/public/share';
const SLOTS: PublicShareSlotName[] = ['before', 'after'];
const VARIANTS: PublicShareVariant[] = ['thumb', 'display', 'full'];
const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
/** The four livestock types (the backend's allow-list and CHECK valid_livestock_type). */
const LIVESTOCK_TYPES: readonly LivestockType[] = ['fish', 'coral', 'invertebrate', 'plant'];

export type PublicShareResult =
  | { status: 'ok'; share: PublicShare }
  /** 404 (or a token that can't be one): no longer shared. */
  | { status: 'gone' }
  /** 429 or 503: try again shortly. */
  | { status: 'busy' }
  /** The network, or anything else the server said. */
  | { status: 'error' };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function textOrNull(value: unknown): string | null | undefined {
  if (value === null || value === undefined) return null;
  return typeof value === 'string' ? value : undefined;
}

type PublicFraming = NonNullable<PublicShareSlot['framing']>;

function finite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * How a shot is lined up (docs/plans/2026-10-29-tank-shot-framing-design.md,
 * 8.1): finite numbers within the bounds the server keeps, the crop's centre
 * on the photo. Anything else draws the shot as taken (null), never refuses
 * the share: framing is only how it is drawn.
 */
export function parseFraming(value: unknown): PublicFraming | null {
  if (!isRecord(value)) return null;
  const { x, y, w, h, rotation } = value;
  if (!finite(x) || !finite(y) || !finite(w) || !finite(h) || !finite(rotation)) return null;
  const sizeOk = (size: number): boolean => size >= MIN_SIZE && size <= MAX_SIZE;
  const centreOk = (start: number, size: number): boolean =>
    start + size / 2 >= 0 && start + size / 2 <= 1;
  if (!sizeOk(w) || !sizeOk(h) || !centreOk(x, w) || !centreOk(y, h)) return null;
  if (Math.abs(rotation) > MAX_ROTATION) return null;
  return { x, y, w, h, rotation };
}

/**
 * One slot, its image paths only on this share's own image route: a response
 * can never make the page load an image from anywhere else.
 */
function parseSlot(
  value: unknown,
  token: string,
  slot: PublicShareSlotName
): PublicShareSlot | null {
  if (!isRecord(value) || typeof value.date !== 'string' || !DAY_PATTERN.test(value.date)) {
    return null;
  }
  const images = value.images;
  if (!isRecord(images)) return null;
  const parsed = {} as Record<PublicShareVariant, string>;
  for (const variant of VARIANTS) {
    const expected = `${PUBLIC_PREFIX}/${token}/image/${slot}/${variant}`;
    if (images[variant] !== expected) return null;
    parsed[variant] = buildApiUrl(expected);
  }
  return { date: value.date, images: parsed, framing: parseFraming(value.framing) };
}

/** A livestock type only when it is one of the four; anything else is no type (never free text). */
function livestockTypeOrNull(value: unknown): LivestockType | null {
  return LIVESTOCK_TYPES.find((type) => type === value) ?? null;
}

/** The response, checked field by field; null when it isn't a share this page can show. */
export function parsePublicShare(body: unknown, token: string): PublicShare | null {
  if (!isRecord(body) || !isRecord(body.slots)) return null;
  if (body.kind !== 'tank_compare' && body.kind !== 'organism_compare') return null;
  const title = textOrNull(body.title);
  const tankName = textOrNull(body.tank_name);
  if (title === undefined || tankName === undefined || typeof body.created_on !== 'string') {
    return null;
  }
  const slots = {} as Record<PublicShareSlotName, PublicShareSlot>;
  for (const slot of SLOTS) {
    const parsed = parseSlot(body.slots[slot], token, slot);
    if (!parsed) return null;
    slots[slot] = parsed;
  }
  const common = {
    title: title?.trim() || null,
    tank_name: tankName?.trim() || null,
    created_on: body.created_on,
    slots,
  };
  if (body.kind === 'tank_compare') return { kind: 'tank_compare', ...common };

  // An organism: the names as text or nothing, the type from the four only.
  const subjectName = textOrNull(body.subject_name);
  const scientificName = textOrNull(body.scientific_name);
  if (subjectName === undefined || scientificName === undefined) return null;
  return {
    kind: 'organism_compare',
    ...common,
    // The server never shows a tank name on an organism link (056's switches CHECK)
    tank_name: null,
    subject_name: subjectName?.trim() || null,
    scientific_name: scientificName?.trim() || null,
    livestock_type: livestockTypeOrNull(body.livestock_type),
  };
}

export async function fetchPublicShare(
  token: string,
  signal?: AbortSignal
): Promise<PublicShareResult> {
  if (!SHARE_TOKEN_PATTERN.test(token)) return { status: 'gone' };
  let response: Response;
  try {
    response = await fetch(buildApiUrl(`${PUBLIC_PREFIX}/${token}`), {
      method: 'GET',
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      signal,
    });
  } catch (error) {
    if (signal?.aborted) throw error;
    return { status: 'error' };
  }
  if (response.status === 404) return { status: 'gone' };
  if (response.status === 429 || response.status === 503) return { status: 'busy' };
  if (!response.ok) return { status: 'error' };
  const body: unknown = await response.json().catch(() => null);
  const share = parsePublicShare(body, token);
  return share ? { status: 'ok', share } : { status: 'error' };
}
